import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, CalendarCheck, Clock, Diamond, Plus, Trash2, Pencil } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTINENTS, GENRES } from "@/lib/data";
import { BookingCard } from "@/components/BookingCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = {
  name: "", genre: "Pop", style: "", continent: "Europe", trend: false, featured: true,
  bio: "", price_from: 15000, price_display: "Sur devis", image_url: "", manager_email: "",
  availability: [], gallery: [], active: true,
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [artists, setArtists] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/artists").then((r) => setArtists(r.data));
    api.get("/bookings").then((r) => setBookings(r.data));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setDialog(true); };
  const openEdit = (a) => {
    setForm({ ...EMPTY, ...a, availability: a.availability || [], gallery: a.gallery || [] });
    setEditId(a.id);
    setDialog(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price_from: Number(form.price_from) || 0,
        availability: typeof form.availability === "string"
          ? form.availability.split(",").map((s) => s.trim()).filter(Boolean)
          : form.availability,
        gallery: typeof form.gallery === "string"
          ? form.gallery.split(",").map((s) => s.trim()).filter(Boolean)
          : form.gallery,
      };
      if (editId) await api.put(`/artists/${editId}`, payload);
      else await api.post("/artists", payload);
      toast.success(editId ? "Artiste mis à jour" : "Artiste ajouté");
      setDialog(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/artists/${id}`); toast.success("Artiste supprimé"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const STAT_CARDS = stats ? [
    { icon: Users, v: stats.artists, l: "Artistes" },
    { icon: CalendarCheck, v: stats.bookings, l: "Demandes" },
    { icon: Clock, v: stats.pending, l: "En attente" },
    { icon: Diamond, v: stats.confirmed, l: "Confirmées" },
  ] : [];

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-14">
      <p className="text-[#C9A24B] text-xs tracking-[0.3em] uppercase mb-2">Administration</p>
      <h1 className="font-serif text-4xl text-white mb-8">Tableau de bord MYSE</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {STAT_CARDS.map((s) => (
          <div key={s.l} className="p-5 rounded-2xl border border-white/10 glass flex items-center gap-4" data-testid={`stat-${s.l}`}>
            <s.icon className="text-[#C9A24B]" size={26} />
            <div>
              <div className="font-serif text-3xl gold-text">{s.v}</div>
              <div className="text-[#A19E95] text-xs uppercase tracking-wider">{s.l}</div>
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="artists">
        <TabsList className="bg-[#15151b] border border-white/10">
          <TabsTrigger value="artists" data-testid="admin-tab-artists" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-black">Artistes</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-black">Demandes</TabsTrigger>
        </TabsList>

        <TabsContent value="artists" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button onClick={openNew} data-testid="add-artist-btn" className="gold-bg text-black font-semibold rounded-full">
              <Plus size={16} className="mr-1" /> Ajouter un artiste
            </Button>
          </div>
          <div className="space-y-3">
            {artists.map((a) => (
              <div key={a.id} className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-[#121216]" data-testid="admin-artist-row">
                <img src={a.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1">
                  <div className="text-white font-serif text-lg">{a.name}</div>
                  <div className="text-[#A19E95] text-xs">{a.genre} · {a.continent} · {a.price_display}</div>
                </div>
                <button onClick={() => openEdit(a)} data-testid={`edit-artist-${a.id}`} className="text-[#C9A24B] hover:bg-[#C9A24B]/10 p-2 rounded-lg"><Pencil size={16} /></button>
                <button onClick={() => remove(a.id)} data-testid={`delete-artist-${a.id}`} className="text-[#FF4D4D] hover:bg-[#FF4D4D]/10 p-2 rounded-lg"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="bookings" className="mt-6">
          {bookings.length === 0 ? (
            <p className="text-[#A19E95] text-center py-16">Aucune demande pour l'instant.</p>
          ) : (
            <div className="space-y-4">
              {bookings.map((b) => (
                <BookingCard key={b.id} b={b}>
                  <p className="text-[#A19E95] text-sm mt-3 pt-3 border-t border-white/5">
                    <span className="text-[#6E6B63]">Client :</span> {b.client_name} · {b.client_email}
                  </p>
                </BookingCard>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#121216] border-[#C9A24B]/25 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editId ? "Modifier" : "Ajouter"} un artiste</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Nom"><Input data-testid="artist-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#181820] border-white/10 text-white" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Genre">
                <Select value={form.genre} onValueChange={(v) => set("genre", v)}>
                  <SelectTrigger className="bg-[#181820] border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181820] border-white/10 text-white">
                    {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Style"><Input data-testid="artist-style" value={form.style} onChange={(e) => set("style", e.target.value)} className="bg-[#181820] border-white/10 text-white" /></Field>
            </div>
            <Field label="Continent">
              <Select value={form.continent} onValueChange={(v) => set("continent", v)}>
                <SelectTrigger className="bg-[#181820] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181820] border-white/10 text-white">
                  {CONTINENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Photo (URL)"><Input data-testid="artist-image" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="bg-[#181820] border-white/10 text-white" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Tarif dès (€)"><Input type="number" data-testid="artist-price" value={form.price_from} onChange={(e) => set("price_from", e.target.value)} className="bg-[#181820] border-white/10 text-white" /></Field>
              <Field label="Affichage tarif"><Input data-testid="artist-price-display" value={form.price_display} onChange={(e) => set("price_display", e.target.value)} className="bg-[#181820] border-white/10 text-white" /></Field>
            </div>
            <Field label="Biographie"><Textarea data-testid="artist-bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} rows={3} className="bg-[#181820] border-white/10 text-white" /></Field>
            <Field label="Disponibilités (dates séparées par des virgules : 2026-07-12, 2026-08-03)">
              <Input data-testid="artist-availability" value={Array.isArray(form.availability) ? form.availability.join(", ") : form.availability} onChange={(e) => set("availability", e.target.value)} className="bg-[#181820] border-white/10 text-white" />
            </Field>
            <Field label="Email manager (lie un compte artiste, optionnel)"><Input data-testid="artist-manager" value={form.manager_email || ""} onChange={(e) => set("manager_email", e.target.value)} className="bg-[#181820] border-white/10 text-white" /></Field>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-[#D8D5CD]"><Switch checked={form.trend} onCheckedChange={(v) => set("trend", v)} data-testid="artist-trend" /> Tendance</label>
              <label className="flex items-center gap-2 text-sm text-[#D8D5CD]"><Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="artist-featured" /> À la une</label>
            </div>
            <Button onClick={save} data-testid="save-artist-btn" className="w-full gold-bg text-black font-semibold rounded-full py-6">
              {editId ? "Enregistrer" : "Ajouter l'artiste"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-white mb-2 block text-sm">{label}</Label>
      {children}
    </div>
  );
}
